/**
 * useParticipantStorage - LocalStorage Persistence for Quiz Participants
 * 
 * Handles storing and retrieving participant data per session to allow
 * rejoining a quiz after page refresh.
 */

import { useState, useEffect, useCallback } from 'react';
import { Logger } from '../../../utils/logger';
import { quizParticipantStorage } from '../../../services/storageService';

const log = Logger.create('useParticipantStorage');

/**
 * Custom hook for participant localStorage persistence
 * @param {string} sessionId - The quiz session ID
 * @returns {Object} Participant storage state and actions
 */
export function useParticipantStorage(sessionId) {
  const [storedData, setStoredData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Load stored data on mount
  useEffect(() => {
    if (!sessionId) {
      setIsLoading(false);
      return;
    }

    const savedData = quizParticipantStorage.get(sessionId);

    if (savedData) {
      if (savedData.participantId && savedData.participantName) {
        setStoredData(savedData);
      } else {
        // Invalid data, remove it
        quizParticipantStorage.remove(sessionId);
        log.warn('Invalid participant data in storage, removed');
      }
    }

    setIsLoading(false);
  }, [sessionId]);

  /**
   * Save participant data to localStorage
   */
  const saveParticipant = useCallback((data) => {
    if (!sessionId) return;

    const dataToStore = {
      participantId: data.participantId,
      participantName: data.participantName,
      totalScore: data.totalScore || 0,
      currentStreak: data.currentStreak || 0,
      savedAt: Date.now()
    };

    try {
      quizParticipantStorage.set(sessionId, dataToStore);
      setStoredData(dataToStore);
    } catch (e) {
      log.error('Failed to save participant data:', e);
    }
  }, [sessionId]);

  /**
   * Update score and streak in localStorage
   */
  const updateScore = useCallback((totalScore, currentStreak, maxStreak) => {
    if (!sessionId || !storedData) return;

    const updatedData = {
      ...storedData,
      totalScore,
      currentStreak,
      maxStreak,
      savedAt: Date.now()
    };

    try {
      quizParticipantStorage.set(sessionId, updatedData);
      setStoredData(updatedData);
    } catch (e) {
      log.error('Failed to update score:', e);
    }
  }, [sessionId, storedData]);

  /**
   * Update jokers in localStorage
   */
  const updateJokers = useCallback((jokersUsed) => {
    if (!sessionId || !storedData) return;

    const updatedData = {
      ...storedData,
      jokersUsed,
      savedAt: Date.now()
    };

    try {
      quizParticipantStorage.set(sessionId, updatedData);
      setStoredData(updatedData);
    } catch (e) {
      log.error('Failed to update jokers:', e);
    }
  }, [sessionId, storedData]);

  /**
   * Clear participant data (logout)
   */
  const clearParticipant = useCallback(() => {
    if (!sessionId) return;

    quizParticipantStorage.remove(sessionId);
    setStoredData(null);
  }, [sessionId]);

  /**
   * Check if participant data exists
   */
  const hasStoredParticipant = storedData?.participantId && storedData?.participantName;

  return {
    // State
    storedData,
    isLoading,
    hasStoredParticipant,

    // Derived values for convenience
    participantId: storedData?.participantId || '',
    participantName: storedData?.participantName || '',
    totalScore: storedData?.totalScore || 0,
    currentStreak: storedData?.currentStreak || 0,
    maxStreak: storedData?.maxStreak || 0,

    // Actions
    saveParticipant,
    updateScore,
    updateJokers,
    clearParticipant,
  };
}

export default useParticipantStorage;
